'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ComponentNode, ComponentStyles } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { DataFetcher } from '../utils/DataFetcher';
import { buildCategoryHref, buildMerchantHref } from '../utils/linkHelpers';
import { getBaseImageUrl } from '@/constants/hooks';

interface SearchFormComponentProps {
    component: ComponentNode;
    context?: {
        companyId?: string;
        domain?: string;
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        searchParams?: { query?: string | string[] };
    };
}

/**
 * `get_meta_keywords` returns a flat array of strings — merchant/search terms
 * such as "Acanva", "AcuRite" — not `{ keyword, merchant }` objects. Reading
 * `item.keyword` off a string is `undefined`, which is why every suggestion
 * used to render blank with "search result" as its only visible text.
 */
type SearchKeyword = string;

interface SearchMerchantItem {
    merchant_name?: string;
    slug?: string;
    website_url?: string;
    merchant_url?: string;
    url?: string;
    merchant_logo?: string;
    logo?: string;
    image?: string;
    promotional_tag?: string;
}

interface SearchCategoryItem {
    name?: string;
    slug?: string;
    category_slug?: string;
    url?: string;
    category_image?: string;
    image?: string;
}

type RequestConfig = {
    apiEndpoint: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: Record<string, any>;
    params?: Record<string, any>;
};

const replaceTemplateValue = (value: any, variables: Record<string, string>): any => {
    if (typeof value === 'string') {
        return value.replace(/\{\{(.*?)\}\}/g, (_, rawKey: string) => {
            const key = rawKey.trim();
            return variables[key] ?? '';
        });
    }

    if (Array.isArray(value)) {
        return value.map((entry) => replaceTemplateValue(entry, variables));
    }

    if (value && typeof value === 'object') {
        const replaced: Record<string, any> = {};
        Object.entries(value).forEach(([key, nested]) => {
            replaced[key] = replaceTemplateValue(nested, variables);
        });
        return replaced;
    }

    return value;
};

const buildRequestConfig = (
    config: RequestConfig | undefined,
    variables: Record<string, string>
): RequestConfig | undefined => {
    if (!config?.apiEndpoint || !config?.method) return undefined;

    return {
        apiEndpoint: replaceTemplateValue(config.apiEndpoint, variables),
        method: config.method,
        body: config.body ? replaceTemplateValue(config.body, variables) : undefined,
        params: config.params ? replaceTemplateValue(config.params, variables) : undefined,
    };
};

const resolveSearchImage = (source: string | undefined, domain: string | undefined): string => {
    if (!source) return '';
    if (/^(https?:)?\/\//i.test(source)) return source;
    if (domain) return getBaseImageUrl(domain, source, source);
    return source.startsWith('/') ? source : `/${source}`;
};

const SearchFormComponent: React.FC<SearchFormComponentProps> = ({ component, context }) => {
    const router = useRouter();
    const { data, styles, customClassName, placeholder } = component;
    const inlineStyles = convertStylesToInline(styles);
    // The submit button used to be drawn with fixed colours and label baked
    // into the JSX, with no field on the node either renderer could read to
    // change it. These come from the same template field the builder writes.
    const buttonStyles = convertStylesToInline(component.searchButtonStyles as ComponentStyles | undefined);
    const buttonLabel = (component.searchButtonLabel as string | undefined)?.trim() || 'Search';
    const searchWrapperRef = useRef<HTMLFormElement | null>(null);
    const formStyles = {
        ...inlineStyles,
        position: 'relative' as const,
        overflow: 'visible',
        zIndex: 60,
    };
    const routeQuery = Array.isArray(context?.searchParams?.query)
        ? context.searchParams.query[0] || ''
        : context?.searchParams?.query || '';
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [keywords, setKeywords] = useState<SearchKeyword[]>([]);
    const [searchMerchants, setSearchMerchants] = useState<SearchMerchantItem[]>([]);
    const [searchCategories, setSearchCategories] = useState<SearchCategoryItem[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchFailed, setSearchFailed] = useState(false);

    const contextVars = useMemo(
        () => ({
            company_id: context?.companyId || '',
            domain: context?.domain || '',
        }),
        [context?.companyId, context?.domain]
    );
    const templateVars = useMemo(
        () => ({ ...contextVars, query: query.trim() }),
        [contextVars, query]
    );

    useEffect(() => {
        setQuery(routeQuery);
    }, [routeQuery]);

    useEffect(() => {
        const fetchKeywords = async () => {
            const suggestionConfig = buildRequestConfig(
                component.suggestionsApiConfig as RequestConfig | undefined,
                contextVars
            );
            if (!suggestionConfig) return;

            try {
                const response = await DataFetcher.fetchRawData(suggestionConfig, {
                    companyId: context?.companyId,
                    domain: context?.domain,
                });
                const incoming = Array.isArray(response?.data) ? response.data : [];
                setKeywords(incoming.filter((entry: unknown): entry is string => typeof entry === 'string' && entry.trim().length > 0));
            } catch (error) {
                console.error('Failed to load search keywords:', error);
                setKeywords([]);
            }
        };

        fetchKeywords();
    }, [component.suggestionsApiConfig, context?.companyId, context?.domain, contextVars]);

    useEffect(() => {
        const normalizedQuery = query.trim();
        if (normalizedQuery.length < 3) {
            setSearchMerchants([]);
            setSearchCategories([]);
            setIsSearching(false);
            setSearchFailed(false);
            return;
        }

        let isCurrent = true;
        setSearchMerchants([]);
        setSearchCategories([]);
        setIsSearching(true);
        setSearchFailed(false);

        const timer = window.setTimeout(async () => {
            const configuredSearch = component.searchApiConfig as RequestConfig | undefined;
            const legacySearch = component.apiConfig as RequestConfig | undefined;
            const searchConfig = buildRequestConfig(
                configuredSearch || legacySearch || {
                    apiEndpoint: '/company/search',
                    method: 'POST',
                    body: { query: '{{query}}' },
                },
                templateVars
            );

            try {
                const response = searchConfig
                    ? await DataFetcher.fetchRawData(searchConfig, {
                        companyId: context?.companyId,
                        domain: context?.domain,
                    })
                    : null;
                if (!isCurrent) return;

                if (!response || response.status === false) {
                    setSearchFailed(true);
                    return;
                }

                const resultData = response.data && typeof response.data === 'object'
                    ? response.data
                    : response;
                setSearchMerchants(Array.isArray(resultData.merchants) ? resultData.merchants : []);
                setSearchCategories(Array.isArray(resultData.categories) ? resultData.categories : []);
            } catch (error) {
                if (!isCurrent) return;
                console.error('Failed to fetch search results:', error);
                setSearchFailed(true);
            } finally {
                if (isCurrent) setIsSearching(false);
            }
        }, 350);

        return () => {
            isCurrent = false;
            window.clearTimeout(timer);
        };
    }, [component.apiConfig, component.searchApiConfig, context?.companyId, context?.domain, query, templateVars]);

    useEffect(() => {
        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (!target) {
                return;
            }

            if (searchWrapperRef.current?.contains(target)) {
                return;
            }

            setIsOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, []);

    const inputPlaceholder =
        typeof data === 'string' && data.trim().length > 0
            ? data
            : typeof placeholder === 'string' && placeholder.trim().length > 0
                ? placeholder
                : 'Search...';

    const displayedItems = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        return keywords
            .filter((term) => !normalizedQuery || term.toLowerCase().includes(normalizedQuery))
            .slice(0, 9);
    }, [keywords, query]);

    // Plain search terms carry no merchant link of their own; each one just
    // reruns the search page with itself as the query.
    const buildItemHref = (term: SearchKeyword): string => `/search?query=${encodeURIComponent(term)}`;

    const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const normalizedQuery = query.trim();
        if (!normalizedQuery) return;
        setIsOpen(false);
        router.push(`/search?query=${encodeURIComponent(normalizedQuery)}`);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    return (
        <form ref={searchWrapperRef} style={formStyles} className={customClassName} onSubmit={submitSearch}>
            <div
                style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                    zIndex: 60,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        minHeight: '42px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '999px',
                        backgroundColor: '#ffffff',
                        overflow: 'hidden',
                    }}
                >
                    <input
                        type="search"
                        name="query"
                        value={query}
                        placeholder={inputPlaceholder}
                        aria-label="Search"
                        onFocus={handleInputFocus}
                        onClick={handleInputFocus}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setIsOpen(true);
                        }}
                        style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            padding: '10px 16px',
                            fontSize: '14px',
                            background: 'transparent',
                        }}
                    />
                    <button
                        type="submit"
                        className="cb-part-search-button"
                        style={{
                            border: 'none',
                            backgroundColor: 'var(--theme-color-primary, #f23017)',
                            color: '#ffffff',
                            padding: '9px 22px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 700,
                            borderRadius: '999px',
                            margin: '1px 2px 1px 0',
                            ...buttonStyles,
                        }}
                    >
                        {buttonLabel}
                    </button>
                </div>

                {isOpen && query.trim().length < 3 && displayedItems.length > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + 10px)',
                            left: 0,
                            width: '100%',
                            backgroundColor: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: '14px',
                            maxHeight: '190px',
                            overflowY: 'auto',
                            zIndex: 120,
                            padding: '8px',
                            boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)',
                        }}
                    >
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {displayedItems.map((term, index) => (
                                <Link
                                    key={`${term}-${index}`}
                                    href={buildItemHref(term)}
                                    onClick={() => setIsOpen(false)}
                                    style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '999px',
                                        padding: '9px 14px',
                                        textDecoration: 'none',
                                        color: '#0f172a',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        display: 'block',
                                        textAlign: 'center',
                                    }}
                                >
                                    {term}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {isOpen && query.trim().length >= 3 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + 10px)',
                            left: 0,
                            width: '100%',
                            backgroundColor: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: '14px',
                            maxHeight: '330px',
                            overflowY: 'auto',
                            zIndex: 120,
                            padding: '8px',
                            boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)',
                        }}
                    >
                        {isSearching ? (
                            <div style={{ fontSize: '13px', color: '#475569', padding: '10px 12px' }}>
                                Searching...
                            </div>
                        ) : searchFailed ? (
                            <div style={{ fontSize: '13px', color: '#b91c1c', padding: '10px 12px' }}>
                                Search is temporarily unavailable.
                            </div>
                        ) : searchMerchants.length === 0 && searchCategories.length === 0 ? (
                            <div style={{ fontSize: '13px', color: '#475569', padding: '10px 12px' }}>
                                No results found.
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gap: '12px' }}>
                                {searchMerchants.length > 0 && (
                                    <section aria-label="Store results">
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', padding: '2px 4px 8px' }}>
                                            Stores
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: '8px' }}>
                                            {searchMerchants.slice(0, 8).map((item, index) => {
                                                const builtHref = buildMerchantHref(item, context);
                                                const href = builtHref !== '#' ? builtHref : item.merchant_url || item.url || '/stores';
                                                const logo = resolveSearchImage(
                                                    item.merchant_logo || item.logo || item.image,
                                                    context?.domain
                                                );
                                                return (
                                                    <Link
                                                        key={`${item.slug || item.merchant_name || 'merchant'}-${index}`}
                                                        href={href}
                                                        onClick={() => setIsOpen(false)}
                                                        style={{
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '12px',
                                                            minWidth: 0,
                                                            minHeight: '100px',
                                                            padding: '10px 8px',
                                                            textDecoration: 'none',
                                                            color: '#0f172a',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: '8px',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {logo ? (
                                                            <img
                                                                src={logo}
                                                                alt=""
                                                                style={{ display: 'block', width: '100%', height: '48px', objectFit: 'contain' }}
                                                            />
                                                        ) : (
                                                            <span aria-hidden="true" style={{ display: 'grid', placeItems: 'center', width: '100%', height: '48px', fontSize: '20px', fontWeight: 700 }}>
                                                                {(item.merchant_name || 'S').slice(0, 1).toUpperCase()}
                                                            </span>
                                                        )}
                                                        <span style={{ display: 'block', width: '100%', fontSize: '12px', fontWeight: 600, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                                                            {item.merchant_name || 'Store'}
                                                        </span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </section>
                                )}
                                {searchCategories.length > 0 && (
                                    <section aria-label="Category results">
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', padding: '2px 4px 8px' }}>
                                            Categories
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: '8px' }}>
                                            {searchCategories.slice(0, 8).map((item, index) => {
                                                const builtHref = buildCategoryHref(item, context);
                                                const href = builtHref !== '#' ? builtHref : item.url || '/category';
                                                const image = resolveSearchImage(
                                                    item.category_image || item.image,
                                                    context?.domain
                                                );
                                                return (
                                                    <Link
                                                        key={`${item.slug || item.name || 'category'}-${index}`}
                                                        href={href}
                                                        onClick={() => setIsOpen(false)}
                                                        style={{
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '12px',
                                                            minWidth: 0,
                                                            minHeight: '100px',
                                                            padding: '10px 8px',
                                                            textDecoration: 'none',
                                                            color: '#0f172a',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: '8px',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {image ? (
                                                            <img src={image} alt="" style={{ display: 'block', width: '100%', height: '48px', objectFit: 'contain' }} />
                                                        ) : (
                                                            <span aria-hidden="true" style={{ display: 'grid', placeItems: 'center', width: '100%', height: '48px', fontSize: '20px', fontWeight: 700 }}>
                                                                {(item.name || 'C').slice(0, 1).toUpperCase()}
                                                            </span>
                                                        )}
                                                        <span style={{ display: 'block', width: '100%', fontSize: '12px', fontWeight: 600, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                                                            {item.name || 'Category'}
                                                        </span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </section>
                                )}
                                <Link
                                    href={`/search?query=${encodeURIComponent(query.trim())}`}
                                    onClick={() => setIsOpen(false)}
                                    style={{
                                        borderTop: '1px solid #e2e8f0',
                                        padding: '10px 4px 2px',
                                        color: '#0f172a',
                                        textDecoration: 'none',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                    }}
                                >
                                    View All Results →
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </form>
    );
};

export default SearchFormComponent;
